<template>
  <div>
    <v-row dense class="no-print">
      <v-col cols="12" sm="4">
        <v-text-field v-model="form.tanggal_awal" type="date" label="Dari Tanggal" outlined clearable dense autofocus @keydown.enter="fetchData" />
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field v-model="form.tanggal_akhir" type="date" label="Sampai Tanggal" outlined clearable dense @keydown.enter="fetchData" />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select v-model="form.spk" :items="spkOptions" label="SPK" outlined dense clearable />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="selectedCategory"
          :items="categoryOptions"
          label="Kategori Pemotongan"
          outlined
          dense
          clearable
          :disabled="!categoryOptions.length"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-btn color="primary" :disabled="!form.tanggal_awal || !form.tanggal_akhir" @click="fetchData">Cari</v-btn>
      </v-col>
    </v-row>

    <v-row v-if="state.loading" class="fill-height" align-content="center" justify="center">
      <v-col class="text-subtitle-1 text-center" cols="12">Loading ...</v-col>
      <v-col cols="6"><v-progress-linear color="primary" indeterminate rounded height="6" /></v-col>
    </v-row>

    <p v-if="state.noData" class="no-print">No Data</p>

    <main v-if="!state.noData && !state.loading" id="section-print" class="report">
      <div class="header">
        <h2 class="report-title">PEMOTONGAN REKAP</h2>
        <h3 class="period-title">Periode: {{ formatDate(form.tanggal_awal) }} s/d {{ formatDate(form.tanggal_akhir) }}</h3>
        <h3 class="period-title">SPK: {{ selectedSpkLabel }}</h3>
        <h3 class="period-title">Kategori: {{ selectedCategory || "Semua Kategori" }}</h3>
      </div>

      <div class="summary">
        <table>
          <tr><td>Total Pemotongan</td><td>:</td><td><b>{{ formatCurrency(displayedTotal) }}</b></td></tr>
          <tr><td>Jumlah Data</td><td>:</td><td>{{ filteredItems.length }}</td></tr>
        </table>
      </div>

      <div class="category-summary mt-3">
        <div v-for="category in visibleCategories" :key="category.kategori" class="category-card">
          <span>{{ category.kategori }} ({{ category.jumlah_data }})</span>
          <b>{{ formatCurrency(category.total) }}</b>
        </div>
      </div>

      <section v-for="category in groupedItems" :key="category.kategori" class="category-section">
        <h3>{{ category.kategori }}</h3>
        <table width="100%" class="table-border">
          <thead>
            <tr>
              <th>Tgl Nota</th><th>Nomor Nota</th><th>Tgl Lunas</th><th>SPK</th>
              <th class="text-right">Jumlah</th><th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in category.items" :key="item.id">
              <td>{{ formatDate(item.tgl_nota) }}</td>
              <td>{{ item.nomor_nota || "-" }}</td>
              <td>{{ formatDate(item.tgl_lunas) }}</td>
              <td>{{ itemSpkLabel(item.port_awal) }}</td>
              <td class="text-right">{{ formatCurrency(item.total_pemotongan) }}</td>
              <td>{{ item.keterangan_pemotongan || "-" }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="text-right"><b>Subtotal {{ category.kategori }}:</b></td>
              <td class="text-right"><b>{{ formatCurrency(category.total) }}</b></td><td></td>
            </tr>
          </tfoot>
        </table>
      </section>
    </main>

    <v-btn v-if="!state.noData && !state.loading" class="mx-2 no-print" fab dark right bottom fixed color="success" @click="printDocument">Print</v-btn>
  </div>
</template>

<script>
import axios from "axios";
import { formatDateOnly } from "@/util/helpers";

const numberValue = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export default {
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      state: { loading: false, noData: true },
      spkOptions: [
        { text: "Pontianak → Jakarta", value: "pontianak_jakarta" },
        { text: "Jakarta → Pontianak", value: "jakarta_pontianak" },
        { text: "Declaring", value: "declaring" }
      ],
      form: {
        tanggal_awal: this.inputDate(firstDay),
        tanggal_akhir: this.inputDate(today),
        spk: null
      },
      selectedCategory: null,
      data: { items: [], per_kategori: [] }
    };
  },
  computed: {
    selectedSpkLabel() {
      return (this.spkOptions.find(item => item.value === this.form.spk) || {}).text || "Semua SPK";
    },
    categoryOptions() {
      return this.data.per_kategori.map(category => category.kategori);
    },
    visibleCategories() {
      return this.selectedCategory
        ? this.data.per_kategori.filter(category => category.kategori === this.selectedCategory)
        : this.data.per_kategori;
    },
    filteredItems() {
      return this.selectedCategory
        ? this.data.items.filter(item => (item.kategori_pemotongan || "Tanpa kategori") === this.selectedCategory)
        : this.data.items;
    },
    displayedTotal() {
      return this.filteredItems.reduce((total, item) => total + numberValue(item.total_pemotongan), 0);
    },
    groupedItems() {
      const groups = this.filteredItems.reduce((result, item) => {
        const category = item.kategori_pemotongan || "Tanpa kategori";
        if (!result[category]) result[category] = [];
        result[category].push(item);
        return result;
      }, {});
      return this.visibleCategories.map(category => ({ ...category, items: groups[category.kategori] || [] }));
    }
  },
  methods: {
    inputDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async fetchData() {
      if (!this.form.tanggal_awal || !this.form.tanggal_akhir) return;
      this.state.loading = true;
      try {
        const response = await axios.get("invoice/laporan-pemotongan", { params: this.form });
        this.data = {
          items: response.data?.items || [],
          per_kategori: response.data?.per_kategori || []
        };
        if (!this.categoryOptions.includes(this.selectedCategory)) this.selectedCategory = null;
        this.state.noData = this.data.items.length === 0;
      } catch (error) {
        this.data = { items: [], per_kategori: [] };
        this.state.noData = true;
        alert(error.response?.data?.message || "Gagal memuat pemotongan rekap");
      } finally {
        this.state.loading = false;
      }
    },
    printDocument() { window.print(); },
    formatDate(value) { return formatDateOnly(value, "/"); },
    formatCurrency(value) { return new Intl.NumberFormat("id-ID").format(numberValue(value)); },
    routeLabel(port) {
      const value = String(port || "").toLowerCase();
      if (value.includes("pontianak") || value.includes("ptk")) return "Pontianak → Jakarta";
      if (value.includes("jakarta") || value.includes("jkt")) return "Jakarta → Pontianak";
      return port || "-";
    },
    itemSpkLabel(port) {
      return this.form.spk ? this.selectedSpkLabel : this.routeLabel(port);
    }
  },
  created() { this.fetchData(); }
};
</script>

<style scoped>
.report { background: white; color: black; font-family: times; padding: 20px; width: 100%; }
.header { margin-bottom: 12px; }
.report-title, .period-title { margin-bottom: 2px; }
.period-title { font-style: italic; }
.summary table { min-width: 320px; }
.category-summary { display: flex; flex-wrap: wrap; gap: 8px; }
.category-card { min-width: 190px; padding: 8px 10px; border: 1px solid #a8a8a8; display: flex; flex-direction: column; }
.category-section { margin-top: 20px; break-inside: avoid; }
.category-section h3 { margin-bottom: 5px; }
.table-border, .table-border th, .table-border td { border: 1px solid #a8a8a8; border-collapse: collapse; }
.table-border th, .table-border td { padding: 4px 6px; }
.table-border th { text-align: left; }
@media print {
  .no-print { display: none !important; }
  .category-section { break-inside: auto; }
  .category-section h3 { break-after: avoid; }
  .table-border thead { display: table-header-group; }
}
</style>
